import { makeEmptyComponentAttributes, makeEmptyEventAttributes, } from '../datalayer';
import { mergeFirstLevel } from './utils';
import { DEFAULT_IMPRESSION_DELAY, FEATURE_APP_NAME_UNKNOWN, ImpressionManager } from '../impression';
export class SharedTrackingServiceV2 {
    datalayer;
    sanitizeUrl;
    logger;
    impressionManager;
    constructor(datalayer, sanitizeUrl, logger) {
        this.datalayer = datalayer;
        this.sanitizeUrl = sanitizeUrl;
        this.logger = logger;
        this.impressionManager = new ImpressionManager(logger, (featureApp, event) => this.track(featureApp, event));
    }
    setFeatureAppName(featureApp) {
        const existingComponent = this.getComponent(featureApp.id);
        if (!existingComponent) {
            this.createComponent(featureApp);
            return;
        }
        existingComponent.componentInfo.componentName = featureApp.name;
    }
    track(featureApp, event) {
        if (!event.eventInfo || typeof event.eventInfo.eventAction !== 'string') {
            this.logger.warn(
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `Dropping event with empty eventAction from ${featureApp.name} (id: ${featureApp.id}). This is most likely a bug in the feature app and should be fixed.`, event);
            return;
        }
        if (!this.datalayer.event) {
            this.datalayer.event = [];
        }
        this.datalayer.event.push(this.addEventAttributes(featureApp, event));
    }
    trackInternalNavigation(featureApp, event, href) {
        const internalNavigationEvent = mergeFirstLevel(event, {
            attributes: {
                targetURL: this.sanitizeUrl(`${window.location.origin}${href}`),
            },
        });
        this.track(featureApp, internalNavigationEvent);
    }
    updateComponent(featureApp, componentUpdate) {
        this.assertComponent(featureApp);
        const componentIndex = this.getComponentIndex(featureApp.id);
        const existingComponent = componentIndex !== -1 ? this.datalayer.component[componentIndex] : undefined;
        const cleanUpdate = JSON.parse(JSON.stringify(componentUpdate)); // deep copy
        // you cannot update componentID and componentName
        delete cleanUpdate.componentInfo?.componentID;
        delete cleanUpdate.componentInfo?.componentName;
        const updatedComponent = mergeFirstLevel(existingComponent || {}, cleanUpdate);
        if (!this.datalayer.component) {
            this.datalayer.component = [];
        }
        if (!existingComponent) {
            this.datalayer.component.push(updatedComponent);
        }
        else {
            this.datalayer.component[componentIndex] = updatedComponent;
        }
    }
    updateUser(userUpdate) {
        // fix user typing mismatch
        if (this.datalayer.user && !Array.isArray(this.datalayer.user)) {
            this.logger.warn(`Migrated datalayer user object to array.`);
            // @ts-ignore
            this.datalayer.user = [this.datalayer.user];
        }
        if (!this.datalayer.user || !this.datalayer.user.length) {
            // set user initially
            this.datalayer.user = [userUpdate];
        }
        else {
            // update existing user using merge
            this.datalayer.user[0] = mergeFirstLevel(this.datalayer.user[0], userUpdate);
        }
    }
    // eslint-disable-next-line class-methods-use-this
    evaluateLinkType(targetUrl) {
        const internalLinkAction = 'internal_link';
        const externalLinkAction = 'exit_link';
        if (targetUrl.startsWith('/')) {
            return internalLinkAction;
        }
        if (targetUrl.startsWith('http')) {
            if (targetUrl.startsWith(window.location.origin)) {
                return internalLinkAction;
            }
            return externalLinkAction;
        }
        return undefined;
    }
    registerImpressionTracking(featureAppInfo, element, createDefaultImpressionEvent, furtherImpressions) {
        const defaultImpressionConfig = {
            createImpressionEvent: createDefaultImpressionEvent,
            delay: DEFAULT_IMPRESSION_DELAY,
        };
        this.assertComponent(featureAppInfo);
        const impressionConfigs = [defaultImpressionConfig];
        if (furtherImpressions) {
            if (Array.isArray(furtherImpressions)) {
                impressionConfigs.push(...furtherImpressions);
            }
            else {
                impressionConfigs.push(furtherImpressions);
            }
        }
        this.impressionManager.add(featureAppInfo, element, impressionConfigs);
        return () => {
            this.impressionManager.remove(element);
        };
    }
    getDatalayer() {
        return this.datalayer;
    }
    getComponentIndex(featureAppId) {
        return this.datalayer.component?.findIndex((component) => component?.componentInfo?.componentID === featureAppId) ?? -1;
    }
    getComponent(featureAppId) {
        return this.datalayer.component?.[this.getComponentIndex(featureAppId)];
    }
    assertComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        throw new Error(`Component for feature app with ID ${featureApp.id} does not exist. Did you forget to set trackingService.featureAppName?`);
    }
    createComponent(featureApp) {
        if (this.getComponent(featureApp.id)) {
            return;
        }
        if (!this.datalayer.component) {
            this.datalayer.component = [];
        }
        this.datalayer.component.push({
            componentInfo: {
                componentID: featureApp.id,
                componentName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
            },
            attributes: makeEmptyComponentAttributes(),
        });
    }
    removeComponent(featureAppId) {
        const componentIndex = this.getComponentIndex(featureAppId);
        if (componentIndex >= 0) {
            this.datalayer.component?.splice(componentIndex, 1);
        }
    }
    addEventAttributes(featureApp, event) {
        this.assertComponent(featureApp);
        const currentURL = this.sanitizeUrl(window.location.href);
        let sanitizedEventAttributes = event.attributes;
        if (event.attributes?.targetURL?.length) {
            sanitizedEventAttributes = mergeFirstLevel(event.attributes, { targetURL: this.sanitizeUrl(event.attributes.targetURL) });
        }
        const autogeneratedAttributes = {
            currentURL,
            relatedComponent: {
                featureAppID: featureApp.id,
                featureAppName: featureApp.name || FEATURE_APP_NAME_UNKNOWN,
                componentIndex: this.getComponentIndex(featureApp.id),
            },
        };
        const attributesWithDefaults = mergeFirstLevel(makeEmptyEventAttributes(), sanitizedEventAttributes ?? {});
        const attributes = mergeFirstLevel(attributesWithDefaults, autogeneratedAttributes);
        return {
            ...event,
            attributes,
        };
    }
}
